import { daysBetween } from "./finance";
import { DomainError } from "./errors";

// ===== Conciliação bancária (T2.10, RF-FIN-02; 06-ia-agentes §3 "Conciliador bancário") e ficha financeira (RF-FIN-05) =====

/** Janela de datas do candidato (dias corridos): a principal (06 §3: ±3 dias) e a ampla, para atrasos/antecipações. */
export const RECONCILE_NEAR_DAYS = 3;
export const RECONCILE_WIDE_DAYS = 30;
/** Candidatos por linha (para a tela e para o Conciliador) e linhas por rodada do Conciliador. */
export const RECONCILE_MAX_CANDIDATES = 5;
export const RECONCILER_MAX_LINES = 50;
/** Títulos casados com uma linha de uma vez (baixa de vários títulos por um só crédito/débito). */
export const RECONCILE_MAX_TITLES = 20;
/** Linhas do extrato por conta financeira (teto de crescimento; 422 `limit_reached` na importação). */
export const MAX_STATEMENT_LINES_PER_ACCOUNT = 200_000;
/** Confiança mínima para a tela destacar a sugestão como forte (pct4: 1.000.000 = 100%). */
export const RECONCILE_STRONG_PCT4 = 800_000;

const STOP = new Set([
  "DE",
  "DA",
  "DO",
  "DAS",
  "DOS",
  "E",
  "LTDA",
  "ME",
  "EPP",
  "SA",
  "S/A",
  "PIX",
  "TED",
  "DOC",
]);

/** Palavras significativas (≥ 3 letras, sem acento, maiúsculas, sem as comuns de extrato). */
export function nameTokens(s: string | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const w of (s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .split(/[^A-Z0-9]+/))
    if (w.length >= 3 && !STOP.has(w) && !/^\d+$/.test(w)) out.add(w);
  return out;
}

/** Fração (0–1) das palavras do nome do contato que aparecem no texto da linha. */
export function nameSimilarity(lineText: string | null, contactName: string | null): number {
  const a = nameTokens(lineText);
  const b = nameTokens(contactName);
  if (a.size === 0 || b.size === 0) return 0;
  let hit = 0;
  for (const w of b) if (a.has(w)) hit++;
  return hit / b.size;
}

/**
 * Texto do banco antes de ir ao modelo (06 §5: dados pessoais mascarados): CPF/CNPJ (com ou sem pontuação), e-mails e
 * sequências longas de dígitos viram `***`; controles e marcação saem; cortado.
 */
export function maskStatementText(s: string | null | undefined, max = 120): string | null {
  if (!s) return null;
  const out = s
    .replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, "***")
    .replace(/\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}/g, "***")
    .replace(/\d{3}\.?\d{3}\.?\d{3}-?\d{2}/g, "***")
    .replace(/\d{8,}/g, "***")
    // eslint-disable-next-line no-control-regex -- texto do banco: remove controles antes do modelo
    .replace(/[\u0000-\u001f\u007f<>]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
  return out || null;
}

export type ReconcileLine = { date: string; amountCents: number; text: string | null };
export type ReconcileCandidate = {
  kind: "title" | "transaction";
  id: string;
  /** Título: saldo em aberto (positivo); lançamento: valor com sinal. */
  amountCents: number;
  /** Título: vencimento; lançamento: data. */
  date: string;
  name: string | null;
};
export type RankedCandidate = ReconcileCandidate & { score: number; daysOff: number };

/**
 * Candidatos de uma linha (06 §3: valor, data ±3 dias, nome): só valor exato (título pelo módulo do valor; lançamento
 * com o mesmo sinal) e data dentro da janela ampla. Pontuação: lançamento já registrado > título; proximidade da data;
 * nome do contato no texto da linha. Os `max` melhores, em ordem.
 */
export function rankCandidates(
  line: ReconcileLine,
  candidates: ReconcileCandidate[],
  max = RECONCILE_MAX_CANDIDATES,
): RankedCandidate[] {
  const abs = Math.abs(line.amountCents);
  const out: RankedCandidate[] = [];
  for (const c of candidates) {
    const sameValue = c.kind === "title" ? c.amountCents === abs : c.amountCents === line.amountCents;
    if (!sameValue) continue;
    const daysOff = Math.abs(daysBetween(c.date, line.date));
    if (daysOff > RECONCILE_WIDE_DAYS) continue;
    const dateScore =
      daysOff <= RECONCILE_NEAR_DAYS ? 1 - daysOff * 0.1 : 0.5 * (1 - daysOff / RECONCILE_WIDE_DAYS);
    const score =
      (c.kind === "transaction" ? 0.4 : 0) + dateScore * 0.5 + nameSimilarity(line.text, c.name) * 0.3;
    out.push({ ...c, score: Math.round(score * 1000) / 1000, daysOff });
  }
  return out
    .sort((a, b) => b.score - a.score || a.daysOff - b.daysOff || a.id.localeCompare(b.id))
    .slice(0, max);
}

/**
 * Casamento da linha com títulos: lado pelo sinal (crédito → a receber, débito → a pagar), sem repetição, até
 * RECONCILE_MAX_TITLES, e a soma dos saldos (BigInt) igual ao valor da linha. Senão `reconcile_mismatch`.
 */
export function assertTitlesMatchLine(
  lineAmountCents: number,
  titles: { id: string; side: "R" | "P"; openCents: number }[],
): void {
  if (titles.length === 0 || titles.length > RECONCILE_MAX_TITLES)
    throw new DomainError("validation", `Escolha de 1 a ${RECONCILE_MAX_TITLES} contas.`);
  if (new Set(titles.map((t) => t.id)).size !== titles.length)
    throw new DomainError("validation", "Conta repetida na seleção.");
  const side = lineAmountCents > 0 ? "R" : "P";
  if (titles.some((t) => t.side !== side))
    throw new DomainError(
      "reconcile_mismatch",
      side === "R"
        ? "Crédito no extrato casa só com contas a receber."
        : "Débito no extrato casa só com contas a pagar.",
    );
  const sum = titles.reduce((s, t) => s + BigInt(t.openCents), 0n);
  if (sum !== BigInt(Math.abs(lineAmountCents)))
    throw new DomainError(
      "reconcile_mismatch",
      "A soma dos saldos das contas escolhidas é diferente do valor do lançamento do extrato.",
    );
}

// ---------- ficha financeira (RF-FIN-05) ----------

/** Limite de crédito do contato (modo do cadastro) e quanto está usado (aberto a receber). */
export function creditLimitUsage(
  mode: "unlimited" | "zero" | "value" | null,
  limitCents: number | null,
  usedCents: number | null,
): {
  mode: "unlimited" | "zero" | "value";
  limitCents: number | null;
  usedCents: number | null;
  availableCents: number | null;
  exceeded: boolean;
} {
  const m = mode ?? "unlimited";
  const limit = m === "value" ? (limitCents ?? 0) : m === "zero" ? 0 : null;
  const used = usedCents;
  const available = limit === null || used === null ? null : limit - used;
  return {
    mode: m,
    limitCents: limit,
    usedCents: used,
    availableCents: available,
    exceeded: limit !== null && used !== null && used > limit,
  };
}
